import React, { useState, useRef, useEffect } from 'react';
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Typography,
  Carousel,
  Tabs,
  message,
  Spin,
} from 'antd';
import type { InputRef } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { login, AddModule } from '../../../redux/slice/userSlice';
import { apiRequest } from '../../../service/apiRequest';
import './login.css';
import { showSuccess, showError } from '../../../components/errorHandler';

import { PROJECT_CONFIGS } from '../../../types/projectConfigs';

type ProjectType = keyof typeof PROJECT_CONFIGS;


const { Title } = Typography;
const { TabPane } = Tabs;
const contentStyle: React.CSSProperties = {
  margin: 0,
  height: '160px',
  color: '#fff',
  lineHeight: '160px',
  textAlign: 'center',
  background: '#364d79',
};

const PROJECT = (process.env.REACT_APP_PROJECT || 'aagam') as ProjectType;
const config = PROJECT_CONFIGS[PROJECT];

// const PROJECT = process.env.REACT_APP_PROJECT;
const Login: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [otpForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mobile, setMobile] = useState('');



  const [activeTab, setActiveTab] = useState<'password' | 'otp'>('password');
  const [otp, setOtp] = useState<string[]>(new Array(4).fill(''));
  const otpRefs = useRef<Array<InputRef | null>>([]);
  const [mobileValidated, setMobileValidated] = useState(false);
  const [isMaster, setisMaster] = useState(false);

  const [resendTimer, setResendTimer] = useState(0);
  const [resendIntervalId, setResendIntervalId] = useState<NodeJS.Timeout | null>(null);


  const handleOtpChange = (index: number, value: string) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleSendOtp = async (values: any) => {
    const mobile = values.mobile;
    if (/^[0-9]{10}$/.test(mobile)) {
      setMobile(mobile);
      try {
        const response = await apiRequest({
          method: 'post',
          url: 'admin-login-via-otp',
          data: {
            mobile: mobile,
            country_code: 91,
          },
        });

        if (response.status == 200) {
          setMobileValidated(true);
          startResendTimer();
          message.success('OTP sent to your mobile');
        } else {
          showError('Login failed: token not received');
        }
      } catch (error: any) {
        showError(
          error.response?.data?.message ||
          `Login failed: ${error.message || 'Unknown error'}`
        );
      } finally {
        setLoading(false);
      }
    } else {
      showError('Please enter a valid 10-digit mobile number');
    }
  };

  const handleOtpSubmit = async () => {
    const otpValue = otp.join('');
    if (otpValue.length === 4) {
      try {
        const response = await apiRequest({
          method: 'post',
          url: 'admin-otp-verify',
          data: {
            otpData: otpValue,
            mobile: mobile,
            country_code: 91,
          },
        });

        if (response.token) {
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('authToken', response.token);

          const userProfile = await apiRequest({
            method: 'get',
            url: `users-by-id`,
          });

          const getModule = await apiRequest({
            method: 'get',
            url: `modules`,
          });

          dispatch(AddModule(getModule));
          dispatch(login(userProfile));
          localStorage.setItem('org_id', userProfile.user_organization_id);

          navigate('/');
        } else {
          showError('Login failed: invalid otp');
        }
      } catch (error: any) {
        showError(
          error.response?.data?.message ||
          `Login failed: ${error.message || 'Unknown error'}`
        );
      } finally {
        setLoading(false);
      }

      // You can make your OTP verification API call here
    } else {
      showError('Please enter all 4 digits of the OTP');
    }
  };
  const startResendTimer = () => {
    setResendTimer(30); // 30 seconds

    const intervalId = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setResendIntervalId(intervalId);
  };

  const resendOTP = async () => {
    if (mobile) {
      try {
        const response = await apiRequest({
          method: 'post',
          url: 'admin-resend-otp',
          data: {
            mobile: mobile,
            country_code: 91,
          },
        });

        if (response.status == 200) {
          message.success(response.message);
          startResendTimer();
        } else {
          showError('Login failed: invalid otp');
        }
      } catch (error: any) {
        showError(
          error.response?.data?.message ||
          `Login failed: ${error.message || 'Unknown error'}`
        );
      } finally {
        setLoading(false);
      }

      // You can make your OTP verification API call here
    } else {
      showError('Please enter all 4 digits of the OTP');
    }
  };

  const handleLogin = async (values: any) => {
    setLoading(true);
    try {
      const response = await apiRequest({
        method: 'post',
        url: 'admin-login',
        data: {
          email: values.email,
          password: values.password,
        },
      });

      if (response.token) {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('authToken', response.token);

        const userProfile = await apiRequest({
          method: 'get',
          url: `users-by-id`,
        });

        const getModule = await apiRequest({
          method: 'get',
          url: `modules`,
        });

        dispatch(AddModule(getModule));
        dispatch(login(userProfile));
        localStorage.setItem('org_id', userProfile.user_organization_id);
        navigate('/');
      } else {
        if (response.role == 'master') {
          setMobile(response.mobile);
          setisMaster(true);
        } else {

          showError('Login failed: token not received');
        }
      }
    } catch (error: any) {
      showError(
        error.response?.data?.message ||
        `Login failed: ${error.message || 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    return () => {
      if (resendIntervalId) clearInterval(resendIntervalId);
    };
  }, [resendIntervalId]);
  const tabItems = [
    {
      key: 'password',
      label: 'Email & Password',
      children: (
        <Spin spinning={loading}>
          <>
            {!isMaster && (
                <Form form={form} layout="vertical" onFinish={handleLogin}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: 'Please input your email!' },
                      { type: 'email', message: 'Please input a valid email!' },
                    ]}
                  >
                    <Input placeholder="Please enter your email id." />
                  </Form.Item>

                  <Form.Item
                    label="Password"
                    name="password"
                    rules={[{ required: true, message: 'Please input your password!' }]}
                  >
                    <Input.Password placeholder="Please enter your password." />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" block>
                      Login
                    </Button>
                  </Form.Item>

                </Form>
            )}

            {isMaster && (
              <div style={{ marginTop: 24 }}>
                <label>Enter OTP</label>
                <div style={{ display: 'flex', gap: 10, marginBottom: 16, justifyContent: 'center' }}>
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      maxLength={1}
                      value={digit}
                      ref={(el) => {
                        otpRefs.current[index] = el;
                      }}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      style={{ width: 40, height: 40, textAlign: 'center' }}
                    />
                  ))}
                </div>
                <Button type="primary" block onClick={handleOtpSubmit}>
                  Submit OTP
                </Button>
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <Button
                    type="link"
                    onClick={resendOTP} // Resend OTP by re-submitting the form
                    disabled={resendTimer > 0}
                  >
                    {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                  </Button>
                </div>
              </div>
            )}
          </>
        </Spin>
      ),
    },
  ];
  // return PROJECT === 'svicon' ? (
  //   <Row style={{
  //     position: 'relative',
  //     backgroundColor: '#f0f2f5',
  //     backgroundImage: 'url(/handicraft.png)',
  //     backgroundSize: 'cover',
  //     backgroundPosition: 'center',
  //     backgroundRepeat: 'no-repeat',
  //     height: '100vh',
  //     width: '100vw',        // full viewport width
  //     margin: 0,             // remove default margin if any
  //     padding: 0,
  //   }}>
  //     {/* Left slider with footer */}
  //     <Col
  //       className="left-slider"
  //       lg={16}
  //       xl={16}
  //       xs={0}
  //       sm={0}
  //       md={0}
  //       style={{
  //         position: 'relative',
  //         backgroundColor: 'transparent', // Fix typo here!
  //       }}>
  //       <div
  //         style={{
  //           position: 'absolute',
  //           top: 20,
  //           left: 20,
  //           fontWeight: 'bold',
  //           color: '#333',
  //         }}
  //       >
  //         <img src="/svicon-logo.png" alt="Logo" style={{ height: 60 }} />
  //       </div>
  //       <div
  //         style={{
  //           position: 'absolute',
  //           bottom: 20,
  //           left: 20,
  //           fontWeight: 'bold',
  //           color: '#333',
  //         }}
  //       >
  //         {/* Developed by {PROJECT} */}
  //       </div>
  //     </Col>

  //     {/* Right login card */}
  //     <Col
  //       xs={24}
  //       md={8}
  //       style={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         padding: '2rem 1rem',
  //         order: 1
  //       }}>
  //       <div style={{ width: '100%', maxWidth: 400, padding: '2rem', backgroundColor: '#fff', borderRadius: '20px' }}>
  //         <Title level={3}><div><strong style={{ color: '#707070' }}>Login</strong></div></Title>
  //         <p style={{ color: '#212529' }}>If you are registered, please use your login credentials.</p>


  //         <Tabs
  //           defaultActiveKey="password"
  //           onChange={(key) => {
  //             setActiveTab(key as 'password' | 'otp');
  //             setMobileValidated(false);
  //             setOtp(new Array(4).fill(''));
  //             form.resetFields();
  //             otpForm.resetFields();
  //           }}
  //           items={tabItems}
  //         />
  //       </div>
  //     </Col>
  //   </Row>
  // ) : (
  //   <Row style={{ minHeight: '100vh' }}>

  //     {/* Left slider with footer */}
  //     <Col
  //       className="left-slider"
  //       lg={16}
  //       xl={16}
  //       xs={0}
  //       sm={0}
  //       md={0}
  //       style={{ position: 'relative', backgroundColor: '#f0f2f5' }}>
  //       <Carousel arrows infinite={false}>
  //         <div>
  //           <div
  //             style={{
  //               height: '100vh',
  //               backgroundImage: 'url(/image1.webp)',
  //               backgroundSize: 'contain',
  //               backgroundPosition: 'center',
  //               backgroundRepeat: 'no-repeat',
  //             }}
  //           />
  //         </div>
  //         <div>
  //           <div
  //             style={{
  //               height: '100vh',
  //               backgroundImage: 'url(/image2.webp)',
  //               backgroundSize: 'contain',
  //               backgroundPosition: 'center',
  //               backgroundRepeat: 'no-repeat',
  //             }}
  //           />
  //         </div>
  //       </Carousel>
  //       <div
  //         style={{
  //           position: 'absolute',
  //           bottom: 20,
  //           left: 20,
  //           fontWeight: 'bold',
  //           color: '#333',
  //         }}
  //       >
  //         {/* Developed by Aagam.com */}
  //       </div>
  //     </Col>

  //     {/* Right login card */}
  //     <Col
  //       xs={24}
  //       md={8}
  //       style={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         padding: '2rem 1rem',
  //         order: 1
  //       }}>
  //       <div style={{ width: '100%', maxWidth: 400, padding: '2rem' }}>
  //         <Title level={3}><div><strong style={{ color: '#005900' }}>Login</strong></div></Title>
  //         <p style={{ color: '#212529' }}>If you are registered, please use your login credentials.</p>


  //         <Tabs
  //           defaultActiveKey="password"
  //           onChange={(key) => {
  //             setActiveTab(key as 'password' | 'otp');
  //             setMobileValidated(false);
  //             setOtp(new Array(4).fill(''));
  //             form.resetFields();
  //             otpForm.resetFields();
  //           }}
  //           items={tabItems}
  //         />
  //       </div>
  //     </Col>
  //   </Row>
  // );
return (
    <>
      <Row
      style={{
        position: 'relative',
        backgroundColor: '#f0f2f5',
        ...(config.backgroundType === 'image'
          ? {
              backgroundImage: `url(${config.background})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : {}),
        height: '100vh',
        width: '100vw',
        margin: 0,
        padding: 0,
      }}
    >
      {/* Left Section */}
      <Col
        className="left-slider"
        lg={16}
        xl={16}
        xs={0}
        sm={0}
        md={0}
        style={{
          position: 'relative',
          backgroundColor: 'transparent',
        }}
      >
        {/* Logo */}
        <div
          style={{
            position: 'absolute',
            top: 20,
            left: 20,
            fontWeight: 'bold',
            color: '#333',
          }}
        >
          <img src={config.logo} alt="Logo" style={{ height: 60 }} />
        </div>

        {/* Carousel if applicable */}
        {config.showCarousel && (
          <Carousel arrows infinite={false}>
              <div >
                <div
                  style={{
                    height: '100vh',
                    backgroundImage: `url(${Array.isArray(config.background) ? config.background[0] : config.background})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
              </div>
          </Carousel>
        )}

        {/* Footer text */}
        {/* <div
          style={{
            position: 'absolute',
            bottom: 20,
            left: 20,
            fontWeight: 'bold',
            color: '#333',
          }}
        >
          {config.developerText}
        </div> */}
      </Col>

      {/* Right login card */}
      <Col
        xs={24}
        md={8}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '2rem 1rem',
          order: 1,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 400,
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '20px',
          }}
        >
          <Title level={3}>
            <strong style={{ color: config.titleColor }}>Login</strong>
          </Title>
          <p style={{ color: '#212529' }}>
            If you are registered, please use your login credentials.
          </p>

          <Tabs
            defaultActiveKey="password"
            onChange={(key) => {
              setActiveTab(key as 'password' | 'otp');
              setMobileValidated(false);
              setOtp(new Array(4).fill(''));
              form.resetFields();
              otpForm.resetFields();
            }}
            items={tabItems}
          />
        </div>
      </Col>
      </Row>
    </>
)

};

export default Login;
